import * as React from 'react';

import { ProductCategoryContext } from '../productCategory/ProductCategoryProvider';
import { HEADER_NAV_HEIGHT } from '../../constants/layout-constants';
import { addMobileScrollHandler, removeMobileScrollHandler } from '../util/mobile-utils';
import OptimizedImage from '../common/OptimizedImage';
import FormatListBulletedRoundedIcon from '@material-ui/icons/FormatListBulletedRounded';
import DisplayModal from '../common/DisplayModal'; 
import CategoryModal from '../productCategory/CategoryModal';
import PropTypes from 'prop-types';

import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';

import { FilteredProductsContext } from '../products/FilteredProductsProvider';

import styles from './ProductFilterCarousel.module.css';

/**
 * Product category carousel with popup modal
 * 
 * The category images are removed when 
 * filter bar goes sticky at 48px/3rem from top
 * 
 * @param {*} currentCategory - initial category
 * @param {function} handleClick - handle category click 
 */
const ProductFilterCarousel = ({
  currentCategory, 
  handleClick
}) => {

  // The FilteredProductsProvider is used/defined only in the /dispensary view
  const { setCategory } = React.useContext(FilteredProductsContext);
  
  // all categories displayed
  const FULL_DISPLAY_WIDTH = 960;
  const [showModal, setShowModal] = React.useState();
  
  const { productCategories } = React.useContext(ProductCategoryContext);

  const filterRef = React.useRef();    
  const carouselRef = React.useRef();
  
  let animateTask = React.createRef();
  // A simple timeout-based animation
  const animateScroll = (scrollAmount) => {
    const carousel = carouselRef.current;
    let scrollProgress = 0;
    // We'll scroll 20px at a time    
    const roundedAmount = Math.floor(scrollAmount/20) * 20;    
    window.clearTimeout(animateTask.current);
    if (carousel) {
      const scrollMore = () => {
        const amount = scrollAmount < 0 ? -20 : 20;
        carousel.scrollBy (amount, 0);
        scrollProgress += 20;
	      if (scrollProgress <= Math.abs(roundedAmount)) {
          animateTask.current = window.setTimeout(scrollMore, 15);
        }
      }
      // initiate scroll
      scrollMore();
    }  
  } 

  // Handle arrow click/tap
  const scrollItems = (direction) => {
    const carousel = carouselRef.current;
    if (carousel) {
      const currentLeft = carousel.scrollLeft;
      const scrollAmount = parseInt(window.innerWidth * .8, 10);
      if (direction < 0) {
        // scroll back some or to 0
        animateScroll(Math.min(scrollAmount, currentLeft) * -1);
      } else {
        // scroll foward some or to end  
        const more = currentLeft + scrollAmount;
        const max = carousel.scrollWidth - carousel.clientWidth;
        animateScroll(Math.min(more, max));
      }
    }      
  }

  // Monitor category scroll
  React.useEffect(() => {
    const wrapper = filterRef.current;
    const carousel = carouselRef.current;
    
    // Hide / show arrows 
    const toggleArrow = (show, styleClass) => {
      if (wrapper) {
        wrapper.classList.toggle(styleClass, show);
      } 
    }  
    
    // debounce
    let task;
    const handleCarouselScroll = () => { 
      window.clearTimeout(task);
      task = window.setTimeout(() => {
        if (carousel) {
          const max = carousel.scrollWidth - carousel.clientWidth;
          // show/hide right arrow
          const isNotMaxScroll = carousel.scrollLeft + 10 < max;
          toggleArrow(isNotMaxScroll, styles.withRightArrow);
          // show/hide left arrow
          const isScrolled = carousel.scrollLeft > 10;
          toggleArrow(isScrolled, styles.withLeftArrow);
        }
      }, 300);
    };
    
    // debounce
    let resizeTask;
    const handleResize = () => {
      window.clearTimeout(resizeTask);
      resizeTask = window.setTimeout(() => {
        if (wrapper && wrapper.clientWidth >= FULL_DISPLAY_WIDTH) {
          toggleArrow(false, styles.withLeftArrow);
          toggleArrow(false, styles.withRightArrow);  
        }
      }, 300);  
    }; 

    // Add scroll/resize listeners
    if (carousel) {
      carousel.addEventListener('scroll', handleCarouselScroll);
      window.addEventListener('resize', handleResize);
    }
    // Initial right arrow display
    if (wrapper && wrapper.clientWidth < FULL_DISPLAY_WIDTH) { 
      toggleArrow(true, styles.withRightArrow);
    }
        
    return () => { 
      if (carousel) {
        carousel.removeEventListener('scroll', handleCarouselScroll);
        window.removeEventListener('resize', handleResize);
      }
    }   
  }, []);   
  
  // Debounce the scroll handling
  const handlerRef = React.useRef(); 

  // 48px is the height of the header nav
  React.useEffect(() => {
    const handleScroll = () => {
      if (handlerRef.current) {
        window.clearTimeout(handlerRef.current);
      }
      handlerRef.current = window.setTimeout(() => {
        if (filterRef.current) {
          /**
           * Carousel height animation on scroll
           *  
           * Here we have to account for filter height growth/shrinkage (1/2 of 50px total)
           * when checking element position ( the top position changes during height animation)
           * 
           * To avoid infinite animation loop we provide 25px of wiggle room.
           */ 
          if (filterRef.current.getBoundingClientRect().top - 25 < HEADER_NAV_HEIGHT) {  
            filterRef.current.setAttribute('data-is-sticky', true);
          } else {
            filterRef.current.setAttribute('data-is-sticky', false);
          }
        }
      }, 200);
    }
    document.addEventListener('scroll', handleScroll);
    addMobileScrollHandler(handleScroll);
    return () => { 
      document.removeEventListener('scroll', handleScroll);
      removeMobileScrollHandler(handleScroll);
    };
  }, []);

  // Scroll the selected category into view (webkit)
  React.useEffect(() => {
    const selected = document.querySelector(`.${styles.selectedCategory}`);
    // Webkit only
    if (selected && selected.scrollIntoViewIfNeeded) {
      // Webkit magic, and the block:'end' option seems key to horizontal centering
      selected.scrollIntoViewIfNeeded({ block:'end' });  
    }
  }, []);

  return (
    <div ref={filterRef} id="category_filter" className={styles.carouselOuter}>
      <div className={`${styles.carouselWrap} ${currentCategory}`}>
        <div className={styles.carouselMenu}
              onClick={() => setShowModal(true)}>
            <FormatListBulletedRoundedIcon fontSize="inherit"/>
        </div>
        {/* blur the edge of last item */}
        <div className={styles.lastItemMask}></div>
        <span className={`${styles.scrollArrow} ${styles.scrollLft}`} onClick={() => scrollItems(-1)}><ChevronLeftRoundedIcon style={{fontSize:30}}/></span>
        <span className={`${styles.scrollArrow} ${styles.scrollRgt}`} onClick={() => scrollItems(1)}><ChevronRightRoundedIcon style={{fontSize:30}}/></span>
        <ul ref={carouselRef} className={styles.carousel}>
          { productCategories?.map((category) => {
              const selectedStyle = currentCategory === category.name ? styles.selectedCategory : '';
              return (
                <li key={`link${category.name}`} 
                    onClick={()=> {
                      /* FilteredProductsProvider */
                      if (setCategory) {
                        setCategory(category.display_name); 
                      }
                      /* update display */
                      handleClick(category); 
                    }} 
                    className={`${styles.carouselItem} ${selectedStyle}`} 
                    data-val={category.name}>
                  <OptimizedImage imgAlt={category.display_name} imgSrc={category.image_url} />
                  <span className={styles.label}>    
                    {category.display_name}
                  </span>
                </li>
              )
          }) }
          <li className={`${styles.carouselItem} ${styles.forPadding}`}></li>
        </ul>  
       
        { showModal &&
          <DisplayModal title="Product Categories" isShrinkToFit closeFn={() => setShowModal(false)}>
            <CategoryModal 
              currentCategory={currentCategory} 
              productCategories={productCategories} 
              handleClick={(categoryName) => { 
                handleClick(categoryName);
                setShowModal(false);
              }} /> 
          </DisplayModal>
        }
      </div>
    </div>  
  )
}

ProductFilterCarousel.propTypes = {
  currentCategory: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired
}

export default ProductFilterCarousel;